/**
 * Printable user statistics report — A4 landscape, two flavours.
 *
 *   ?mode=admin  – financial / billing focus. Realization, invoiced
 *                  revenue, WIP, target progress, per-matter realization,
 *                  YoY revenue chart.
 *
 *   ?mode=user   – hours / activity focus. Utilization, days worked,
 *                  weekly pattern, billable vs non-billable monthly,
 *                  YoY hours, matters worked on. No money.
 *
 * Both modes auto-trigger ``window.print()`` once data is loaded. Charts
 * are rendered with Recharts (SVG) with animations disabled so the
 * snapshot is captured cleanly by the print pipeline.
 *
 * Supports period=year|quarter|month|custom. For custom, the caller must
 * pass start_date / end_date query params; otherwise the request 400s.
 */
import { useEffect, useMemo, useState } from "react"
import { useParams, useSearchParams } from "react-router-dom"
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ComposedChart,
  Legend,
  Line,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts"

import { Button } from "@/components/ui/button"
import { useDateFormat } from "@/lib/dates"
import { useToast } from "@/hooks/use-toast"
import api from "@/lib/api"
import { ROUTES } from "@/constants/routes"

type Mode = "admin" | "user"

// ─── Formatting helpers ──────────────────────────────────────────────────────

const fmtNum = (v: any, digits = 1): string => {
  const n = typeof v === "string" ? parseFloat(v) : v ?? 0
  if (!Number.isFinite(n)) return "—"
  return n.toFixed(digits)
}

const fmtInt = (v: any): string => fmtNum(v, 0)

const fmtPct = (v: any): string => {
  const n = typeof v === "string" ? parseFloat(v) : v ?? 0
  if (!Number.isFinite(n)) return "—"
  return `${Math.round(n)}%`
}

const fmtEur = (v: any): string => {
  const n = typeof v === "string" ? parseFloat(v) : v ?? 0
  if (!Number.isFinite(n)) return "—"
  return new Intl.NumberFormat(undefined, {
    style: "currency",
    currency: "EUR",
    maximumFractionDigits: 0,
  }).format(n)
}

const fmtEurK = (v: any): string => {
  const n = typeof v === "string" ? parseFloat(v) : v ?? 0
  if (!Number.isFinite(n)) return "—"
  if (Math.abs(n) >= 1000) return `€${(n / 1000).toFixed(0)}k`
  return `€${Math.round(n)}`
}

// Print-friendly palette: high contrast, ink-saving fills.
const COLOR = {
  primary: "#1f2937",        // slate-800 — body text / axes
  accent: "#2563eb",         // blue-600 — primary metric
  accentSoft: "#bfdbfe",     // blue-200 — comparator
  positive: "#16a34a",       // green-600
  positiveSoft: "#bbf7d0",   // green-200
  warning: "#d97706",        // amber-600
  danger: "#dc2626",          // red-600
  neutral: "#6b7280",        // gray-500
  neutralSoft: "#e5e7eb",    // gray-200
  grid: "#e5e7eb",
}

// ─── Component ───────────────────────────────────────────────────────────────

export function UserReportPrintable() {
  const { userId } = useParams<{ userId: string }>()
  const [searchParams] = useSearchParams()
  const mode = (searchParams.get("mode") as Mode) || "admin"
  const period = searchParams.get("period") || "year"
  const startDate = searchParams.get("start_date")
  const endDate = searchParams.get("end_date")
  const compareStart = searchParams.get("compare_start_date")
  const compareEnd = searchParams.get("compare_end_date")

  const { format: formatDate } = useDateFormat()
  const { toast } = useToast()
  const [stats, setStats] = useState<any>(null)
  const [userInfo, setUserInfo] = useState<any>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (!userId) return
    let cancelled = false
    async function load() {
      setLoading(true)
      try {
        const params: Record<string, string> = { period, detail: "full" }
        if (period === "custom" && startDate && endDate) {
          params.start_date = startDate
          params.end_date = endDate
        }
        if (compareStart && compareEnd) {
          params.compare_start_date = compareStart
          params.compare_end_date = compareEnd
        }
        const [statsResp, userResp] = await Promise.all([
          api.get(`${ROUTES.API.STATISTICS}users/${userId}/`, { params }),
          api.get(`${ROUTES.API.USERS}${userId}/`),
        ])
        if (cancelled) return
        setStats(statsResp.data)
        setUserInfo(userResp.data)
      } catch (e: any) {
        toast({
          title: "Could not build report",
          description: e?.message ?? "Permission denied?",
          variant: "destructive",
        })
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    load()
    return () => {
      cancelled = true
    }
  }, [userId, period, startDate, endDate, compareStart, compareEnd])

  // Trigger the browser print dialog once the report has rendered. Wait
  // a beat longer than the original (charts need a paint).
  useEffect(() => {
    if (loading || !stats) return
    const t = window.setTimeout(() => window.print(), 600)
    return () => window.clearTimeout(t)
  }, [loading, stats])

  // Pull raw helpers, falling back from `_actual` aliases (in-progress
  // periods) to bare values (complete periods) for safety.
  const time = stats?.time_metrics ?? {}
  const fin = stats?.financial_metrics ?? {}
  const target = stats?.target_tracking ?? {}
  const matters: any[] = stats?.matter_work?.matter_distribution ?? []
  const clients: any[] = stats?.client_work?.client_distribution ?? []
  const monthly: any[] = stats?.monthly_breakdown ?? []
  const yearly = stats?.year_comparison
  const weekly: any[] = stats?.weekly_patterns ?? []
  const compPeriod = stats?.comparison_period

  const pickHours = (key: string) => time[`${key}_actual`] ?? time[key] ?? 0
  const pickEur = (key: string) => fin[`${key}_actual`] ?? fin[key] ?? 0

  const periodLabel = useMemo(() => {
    if (!stats?.period) return period
    const ps = stats.period.start_date
    const pe = stats.period.end_date
    if (!ps || !pe) return period
    return `${formatDate(new Date(ps))} – ${formatDate(new Date(pe))}`
  }, [stats, period, formatDate])

  // Prepare YoY chart data.
  const yoyData = useMemo(() => {
    if (!yearly?.current_year?.data) return []
    return yearly.current_year.data.map((cur: any, i: number) => {
      const prev = yearly.previous_year?.data?.[i]
      return {
        month: cur.month_short,
        currentHours: cur.billable_hours,
        previousHours: prev?.billable_hours ?? 0,
        currentRevenue: cur.revenue,
        previousRevenue: prev?.revenue ?? 0,
      }
    })
  }, [yearly])

  // Target chart — cumulative billable hours vs prorated yearly target.
  const targetChart = useMemo(() => {
    if (!monthly?.length || !target?.yearly_target) return []
    const monthlyTarget = target.yearly_target / 12
    let cumActual = 0
    let cumTarget = 0
    return monthly.map((m: any) => {
      cumActual += m.billable_hours
      cumTarget += monthlyTarget
      return {
        month: m.month_short,
        actual: parseFloat(m.billable_hours.toFixed(1)),
        target: parseFloat(monthlyTarget.toFixed(1)),
        cumActual: parseFloat(cumActual.toFixed(1)),
        cumTarget: parseFloat(cumTarget.toFixed(1)),
        ahead: m.billable_hours >= monthlyTarget,
      }
    })
  }, [monthly, target?.yearly_target])

  if (!userId) return null

  const yearlyTarget = target?.yearly_target ?? 0
  const ytdHours = target?.ytd_billable_hours ?? 0
  const targetPct = yearlyTarget > 0 ? (ytdHours / yearlyTarget) * 100 : 0
  const aheadBehind = target?.hours_ahead_behind ?? 0

  // Derived realization vs invoicing block.
  const billableHours = pickHours("billable_hours")
  const invoicedHours = time.invoiced_hours ?? 0
  const wipHours = time.wip_hours ?? Math.max(0, billableHours - invoicedHours)
  const realization = billableHours > 0 ? (invoicedHours / billableHours) * 100 : 0

  // Color matters by realization for admin tables.
  const realizationColor = (pct: number) => {
    if (pct >= 80) return COLOR.positive
    if (pct >= 50) return COLOR.warning
    return COLOR.danger
  }

  return (
    <div className="printable-report mx-auto max-w-[1180px] p-6 text-[12px] text-slate-900">
      {/* Controls visible on screen only. */}
      <div className="no-print mb-6 flex items-center justify-between gap-3 rounded-md border bg-muted/40 p-3">
        <div className="text-sm text-muted-foreground">
          {loading
            ? "Loading…"
            : "Report ready. If the print dialog didn't open automatically, click Print."}
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => window.close()}>
            Close
          </Button>
          <Button onClick={() => window.print()}>Print</Button>
        </div>
      </div>

      {/* ── Header ───────────────────────────────────────────────────────── */}
      <header className="mb-5 flex items-start justify-between gap-6 border-b-2 border-slate-900 pb-4">
        <div className="flex-1">
          <div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-500">
            {mode === "admin" ? "Performance & Billing Report" : "Activity Report"}
          </div>
          <h1 className="mt-1 text-[26px] font-bold leading-tight tracking-tight text-slate-900">
            {userInfo ? `${userInfo.first_name} ${userInfo.last_name}` : "—"}
          </h1>
          <div className="mt-1 text-[12px] text-slate-600">
            {userInfo?.email}
            {userInfo?.title ? ` · ${userInfo.title}` : ""}
            {stats?.user_info?.firm ? ` · ${stats.user_info.firm}` : ""}
          </div>
        </div>
        <div className="text-right">
          <div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-500">
            Reporting Period
          </div>
          <div className="mt-1 text-[16px] font-semibold text-slate-900">{periodLabel}</div>
          <div className="mt-1 text-[11px] text-slate-500">
            Generated {formatDate(new Date())}
          </div>
        </div>
      </header>

      {/* ── KPI Strip ────────────────────────────────────────────────────── */}
      <section className="mb-5 grid grid-cols-4 gap-3">
        {mode === "admin" ? (
          <>
            <Stat label="Billable Hours" value={`${fmtNum(billableHours)}h`} accent />
            <Stat label="Invoiced Hours" value={`${fmtNum(invoicedHours)}h`} />
            <Stat
              label="WIP Hours"
              value={`${fmtNum(wipHours)}h`}
              hint={wipHours > 0 ? "Pending invoicing" : undefined}
            />
            <Stat
              label="Realization"
              value={fmtPct(realization)}
              valueColor={realizationColor(realization)}
              hint="Invoiced / billable"
            />
            <Stat label="Invoiced Revenue" value={fmtEur(fin.invoiced_revenue)} accent />
            <Stat label="Billable Revenue" value={fmtEur(pickEur("billable_revenue"))} />
            <Stat label="Avg Rate" value={`${fmtEur(fin.average_rate)}/h`} />
            <Stat label="Revenue / Hour" value={`${fmtEur(fin.revenue_per_hour)}/h`} />
          </>
        ) : (
          <>
            <Stat label="Total Hours" value={`${fmtNum(pickHours("total_hours"))}h`} accent />
            <Stat label="Billable Hours" value={`${fmtNum(billableHours)}h`} />
            <Stat label="Non-Billable" value={`${fmtNum(pickHours("non_billable_hours"))}h`} />
            <Stat
              label="Utilization"
              value={fmtPct(time.utilization_rate)}
              valueColor={realizationColor(time.utilization_rate ?? 0)}
              hint="Billable / total"
            />
            <Stat label="Days Worked" value={fmtInt(time.days_worked)} />
            <Stat label="Avg / Day" value={`${fmtNum(time.average_hours_per_day)}h`} />
            <Stat label="Billable / Day" value={`${fmtNum(time.average_billable_hours_per_day)}h`} />
            <Stat label="Entries" value={fmtInt(pickHours("entries_count"))} />
          </>
        )}
      </section>

      {/* ── Period Comparison (when explicitly comparing two ranges) ─────── */}
      {compPeriod && (
        <section className="page-keep mb-5 rounded-md border border-slate-200 bg-slate-50 p-4">
          <SectionHeading>Period Comparison</SectionHeading>
          <div className="mt-2 grid grid-cols-4 gap-3">
            <CompareStat
              label="Billable hours"
              current={compPeriod ? billableHours : 0}
              compare={compPeriod?.time_metrics?.billable_hours_actual ?? compPeriod?.time_metrics?.billable_hours ?? 0}
              format="hours"
            />
            <CompareStat
              label="Total hours"
              current={pickHours("total_hours")}
              compare={compPeriod?.time_metrics?.total_hours_actual ?? compPeriod?.time_metrics?.total_hours ?? 0}
              format="hours"
            />
            <CompareStat
              label="Utilization"
              current={time.utilization_rate ?? 0}
              compare={compPeriod?.time_metrics?.utilization_rate ?? 0}
              format="pct"
            />
            {mode === "admin" && (
              <CompareStat
                label="Revenue"
                current={pickEur("total_revenue")}
                compare={compPeriod?.financial_metrics?.total_revenue_actual ?? compPeriod?.financial_metrics?.total_revenue ?? 0}
                format="eur"
              />
            )}
          </div>
        </section>
      )}

      {/* ── Target Tracking (admin only) ─────────────────────────────────── */}
      {mode === "admin" && yearlyTarget > 0 && (
        <section className="page-keep mb-5 grid grid-cols-3 gap-4">
          <div className="col-span-2 rounded-md border border-slate-200 p-4">
            <SectionHeading>
              Billable Hours vs Yearly Target
              <span className="ml-2 text-[10px] font-normal text-slate-500">
                Cumulative · target line at €{fmtNum(yearlyTarget / 12)}h/month
              </span>
            </SectionHeading>
            {targetChart.length > 0 ? (
              <ResponsiveContainer width="100%" height={210}>
                <ComposedChart data={targetChart} margin={{ top: 10, right: 16, left: 0, bottom: 0 }}>
                  <CartesianGrid stroke={COLOR.grid} vertical={false} />
                  <XAxis dataKey="month" tick={{ fontSize: 11, fill: COLOR.primary }} />
                  <YAxis tick={{ fontSize: 11, fill: COLOR.primary }} />
                  <Tooltip formatter={(v: any) => `${fmtNum(v)}h`} />
                  <Legend wrapperStyle={{ fontSize: 11 }} iconSize={10} />
                  <ReferenceLine
                    y={yearlyTarget}
                    stroke={COLOR.danger}
                    strokeDasharray="4 4"
                    label={{ value: `Year target ${fmtInt(yearlyTarget)}h`, position: "insideTopRight", fill: COLOR.danger, fontSize: 10 }}
                  />
                  <Line
                    type="monotone"
                    dataKey="cumTarget"
                    stroke={COLOR.danger}
                    strokeDasharray="4 4"
                    strokeWidth={2}
                    name="Expected (cumulative)"
                    dot={false}
                    isAnimationActive={false}
                  />
                  <Bar dataKey="cumActual" name="Actual (cumulative)" radius={[3, 3, 0, 0]} maxBarSize={32} isAnimationActive={false}>
                    {targetChart.map((d: any, i: number) => (
                      <Cell key={i} fill={d.cumActual >= d.cumTarget ? COLOR.positive : COLOR.accent} />
                    ))}
                  </Bar>
                </ComposedChart>
              </ResponsiveContainer>
            ) : (
              <p className="py-8 text-center text-sm text-slate-500">No target data available.</p>
            )}
          </div>
          <div className="rounded-md border border-slate-200 p-4">
            <SectionHeading>Year-to-Date</SectionHeading>
            <div className="mt-3 space-y-3">
              <ProgressBar
                label="YTD Billable"
                value={ytdHours}
                max={yearlyTarget}
                valueLabel={`${fmtInt(ytdHours)}h / ${fmtInt(yearlyTarget)}h`}
                color={ytdHours >= (target?.expected_hours ?? 0) ? COLOR.positive : COLOR.warning}
              />
              <div className="flex items-baseline justify-between border-t border-slate-100 pt-3">
                <span className="text-[11px] uppercase tracking-wide text-slate-500">Progress</span>
                <span className="text-[18px] font-bold tabular-nums">{fmtPct(targetPct)}</span>
              </div>
              <div className="flex items-baseline justify-between">
                <span className="text-[11px] uppercase tracking-wide text-slate-500">Pace</span>
                <span
                  className="text-[14px] font-semibold tabular-nums"
                  style={{ color: aheadBehind >= 0 ? COLOR.positive : COLOR.danger }}
                >
                  {aheadBehind >= 0 ? "+" : ""}
                  {fmtNum(aheadBehind, 0)}h vs expected
                </span>
              </div>
              <div className="flex items-baseline justify-between">
                <span className="text-[11px] uppercase tracking-wide text-slate-500">Monthly target</span>
                <span className="text-[12px] tabular-nums">{fmtNum(target?.monthly_target)}h</span>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* ── Charts row 1: Monthly hours / revenue ─────────────────────────── */}
      <section className="page-keep mb-5 grid grid-cols-2 gap-4">
        {/* Monthly hours: stacked billable / non-billable */}
        {monthly.length > 0 && (
          <div className="rounded-md border border-slate-200 p-4">
            <SectionHeading>Monthly Hours — Billable vs Non-Billable</SectionHeading>
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={monthly} margin={{ top: 10, right: 12, left: 0, bottom: 0 }}>
                <CartesianGrid stroke={COLOR.grid} vertical={false} />
                <XAxis dataKey="month_short" tick={{ fontSize: 11, fill: COLOR.primary }} />
                <YAxis tick={{ fontSize: 11, fill: COLOR.primary }} />
                <Tooltip formatter={(v: any) => `${fmtNum(v)}h`} />
                <Legend wrapperStyle={{ fontSize: 11 }} iconSize={10} />
                <Bar
                  dataKey="billable_hours"
                  stackId="h"
                  fill={COLOR.positive}
                  name="Billable"
                  radius={[0, 0, 0, 0]}
                  isAnimationActive={false}
                />
                <Bar
                  dataKey="non_billable_hours"
                  stackId="h"
                  fill={COLOR.warning}
                  name="Non-billable"
                  radius={[3, 3, 0, 0]}
                  isAnimationActive={false}
                />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}

        {/* Mode-specific second chart */}
        {mode === "admin" ? (
          monthly.length > 0 && (
            <div className="rounded-md border border-slate-200 p-4">
              <SectionHeading>Monthly Revenue</SectionHeading>
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={monthly} margin={{ top: 10, right: 12, left: 0, bottom: 0 }}>
                  <CartesianGrid stroke={COLOR.grid} vertical={false} />
                  <XAxis dataKey="month_short" tick={{ fontSize: 11, fill: COLOR.primary }} />
                  <YAxis
                    tickFormatter={fmtEurK}
                    tick={{ fontSize: 11, fill: COLOR.primary }}
                  />
                  <Tooltip formatter={(v: any) => fmtEur(v)} />
                  <Bar dataKey="revenue" fill={COLOR.accent} name="Revenue" radius={[3, 3, 0, 0]} isAnimationActive={false} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )
        ) : (
          weekly.length > 0 && (
            <div className="rounded-md border border-slate-200 p-4">
              <SectionHeading>Weekly Pattern — Average Hours by Day</SectionHeading>
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={weekly} margin={{ top: 10, right: 12, left: 0, bottom: 0 }}>
                  <CartesianGrid stroke={COLOR.grid} vertical={false} />
                  <XAxis
                    dataKey="day"
                    tick={{ fontSize: 11, fill: COLOR.primary }}
                    tickFormatter={(v: string) => v.slice(0, 3)}
                  />
                  <YAxis tick={{ fontSize: 11, fill: COLOR.primary }} />
                  <Tooltip formatter={(v: any) => `${fmtNum(v)}h`} />
                  <Legend wrapperStyle={{ fontSize: 11 }} iconSize={10} />
                  <Bar dataKey="billable_hours" stackId="d" fill={COLOR.positive} name="Billable" isAnimationActive={false} />
                  <Bar dataKey="non_billable_hours" stackId="d" fill={COLOR.warning} name="Non-billable" radius={[3, 3, 0, 0]} isAnimationActive={false} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )
        )}
      </section>

      {/* ── Year-over-Year (when available) ──────────────────────────────── */}
      {yoyData.length > 0 && (
        <section className="page-keep mb-5 grid grid-cols-2 gap-4">
          <div className="rounded-md border border-slate-200 p-4">
            <SectionHeading>
              Hours — Year over Year
              <span className="ml-2 text-[10px] font-normal text-slate-500">
                {yearly?.previous_year?.label} vs {yearly?.current_year?.label}
              </span>
            </SectionHeading>
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={yoyData} margin={{ top: 10, right: 12, left: 0, bottom: 0 }}>
                <CartesianGrid stroke={COLOR.grid} vertical={false} />
                <XAxis dataKey="month" tick={{ fontSize: 11, fill: COLOR.primary }} />
                <YAxis tick={{ fontSize: 11, fill: COLOR.primary }} />
                <Tooltip formatter={(v: any) => `${fmtNum(v)}h`} />
                <Legend wrapperStyle={{ fontSize: 11 }} iconSize={10} />
                <Bar dataKey="previousHours" name={yearly?.previous_year?.label ?? "Previous"} fill={COLOR.accentSoft} radius={[3, 3, 0, 0]} isAnimationActive={false} />
                <Bar dataKey="currentHours" name={yearly?.current_year?.label ?? "Current"} fill={COLOR.accent} radius={[3, 3, 0, 0]} isAnimationActive={false} />
              </BarChart>
            </ResponsiveContainer>
          </div>

          {mode === "admin" ? (
            <div className="rounded-md border border-slate-200 p-4">
              <SectionHeading>
                Revenue — Year over Year
                <span className="ml-2 text-[10px] font-normal text-slate-500">
                  {yearly?.previous_year?.label} vs {yearly?.current_year?.label}
                </span>
              </SectionHeading>
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={yoyData} margin={{ top: 10, right: 12, left: 0, bottom: 0 }}>
                  <CartesianGrid stroke={COLOR.grid} vertical={false} />
                  <XAxis dataKey="month" tick={{ fontSize: 11, fill: COLOR.primary }} />
                  <YAxis tickFormatter={fmtEurK} tick={{ fontSize: 11, fill: COLOR.primary }} />
                  <Tooltip formatter={(v: any) => fmtEur(v)} />
                  <Legend wrapperStyle={{ fontSize: 11 }} iconSize={10} />
                  <Bar dataKey="previousRevenue" name={yearly?.previous_year?.label ?? "Previous"} fill={COLOR.positiveSoft} radius={[3, 3, 0, 0]} isAnimationActive={false} />
                  <Bar dataKey="currentRevenue" name={yearly?.current_year?.label ?? "Current"} fill={COLOR.positive} radius={[3, 3, 0, 0]} isAnimationActive={false} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          ) : (
            yoyData.length > 0 && (
              <div className="rounded-md border border-slate-200 p-4">
                <SectionHeading>Cumulative Billable Hours</SectionHeading>
                <ResponsiveContainer width="100%" height={200}>
                  <ComposedChart
                    data={(() => {
                      let cur = 0
                      let prev = 0
                      return yoyData.map((r: any) => {
                        cur += r.currentHours
                        prev += r.previousHours
                        return { month: r.month, current: cur, previous: prev }
                      })
                    })()}
                    margin={{ top: 10, right: 12, left: 0, bottom: 0 }}
                  >
                    <CartesianGrid stroke={COLOR.grid} vertical={false} />
                    <XAxis dataKey="month" tick={{ fontSize: 11, fill: COLOR.primary }} />
                    <YAxis tick={{ fontSize: 11, fill: COLOR.primary }} />
                    <Tooltip formatter={(v: any) => `${fmtNum(v)}h`} />
                    <Legend wrapperStyle={{ fontSize: 11 }} iconSize={10} />
                    <Line type="monotone" dataKey="previous" stroke={COLOR.neutral} strokeWidth={2} name={yearly?.previous_year?.label ?? "Previous"} dot={false} isAnimationActive={false} />
                    <Line type="monotone" dataKey="current" stroke={COLOR.accent} strokeWidth={2} name={yearly?.current_year?.label ?? "Current"} dot={false} isAnimationActive={false} />
                  </ComposedChart>
                </ResponsiveContainer>
              </div>
            )
          )}
        </section>
      )}

      {/* ── Matter Breakdown ─────────────────────────────────────────────── */}
      <section className="page-break-before mb-5">
        <SectionHeading>
          Matter Breakdown
          <span className="ml-2 text-[10px] font-normal text-slate-500">
            {matters.length} matters
          </span>
        </SectionHeading>
        {matters.length === 0 ? (
          <p className="mt-2 text-sm text-slate-500">No matter activity this period.</p>
        ) : (
          <table className="mt-2 w-full border-collapse text-[11px]">
            <thead>
              <tr className="border-b-2 border-slate-900 text-left text-[10px] uppercase tracking-wide text-slate-600">
                <th className="py-1.5 pr-2">Matter</th>
                <th className="py-1.5 px-2 text-right">Total h</th>
                <th className="py-1.5 px-2 text-right">Billable h</th>
                {mode === "admin" ? (
                  <>
                    <th className="py-1.5 px-2 text-right">Invoiced h</th>
                    <th className="py-1.5 px-2 text-right">Realization</th>
                    <th className="py-1.5 px-2 text-right">Billable €</th>
                    <th className="py-1.5 px-2 text-right">Invoiced €</th>
                    <th className="py-1.5 pl-2 text-right">Budget</th>
                  </>
                ) : (
                  <>
                    <th className="py-1.5 px-2 text-right">Entries</th>
                    <th className="py-1.5 px-2 text-right">% of period</th>
                    <th className="py-1.5 pl-2 text-right">Last activity</th>
                  </>
                )}
              </tr>
            </thead>
            <tbody>
              {(() => {
                const totalBillable =
                  matters.reduce((s, m) => s + (m.billable_hours || 0), 0) || 1
                return matters
                  .filter((m) => (m.billable_hours ?? 0) > 0 || (m.total_hours ?? 0) > 0)
                  .sort((a, b) => (b.billable_hours ?? 0) - (a.billable_hours ?? 0))
                  .map((m) => {
                    const r = m.realization_rate ?? 0
                    const pct = ((m.billable_hours ?? 0) / totalBillable) * 100
                    return (
                      <tr key={m["matter__id"]} className="border-b border-slate-100">
                        <td className="py-1.5 pr-2">
                          <div className="font-medium text-slate-900">
                            {m.matter_label || m.matter__name || "Unknown"}
                          </div>
                        </td>
                        <td className="py-1.5 px-2 text-right tabular-nums">
                          {fmtNum(m.total_hours)}h
                        </td>
                        <td className="py-1.5 px-2 text-right font-semibold tabular-nums">
                          {fmtNum(m.billable_hours)}h
                        </td>
                        {mode === "admin" ? (
                          <>
                            <td className="py-1.5 px-2 text-right tabular-nums">
                              {fmtNum(m.invoiced_hours)}h
                            </td>
                            <td className="py-1.5 px-2 text-right tabular-nums">
                              <span style={{ color: realizationColor(r) }} className="font-semibold">
                                {fmtPct(r)}
                              </span>
                            </td>
                            <td className="py-1.5 px-2 text-right tabular-nums">
                              {fmtEur(m.billable_revenue)}
                            </td>
                            <td className="py-1.5 px-2 text-right font-semibold tabular-nums">
                              {fmtEur(m.invoiced_revenue)}
                            </td>
                            <td className="py-1.5 pl-2 text-right tabular-nums">
                              {m.budget_utilization > 0 ? (
                                <span
                                  style={{
                                    color:
                                      m.budget_utilization > 100
                                        ? COLOR.danger
                                        : m.budget_utilization > 80
                                          ? COLOR.warning
                                          : COLOR.positive,
                                  }}
                                  className="font-semibold"
                                >
                                  {fmtPct(m.budget_utilization)}
                                </span>
                              ) : (
                                <span className="text-slate-400">—</span>
                              )}
                            </td>
                          </>
                        ) : (
                          <>
                            <td className="py-1.5 px-2 text-right tabular-nums">{m.entries}</td>
                            <td className="py-1.5 px-2 text-right tabular-nums">
                              <div className="flex items-center justify-end gap-1.5">
                                <span className="tabular-nums">{fmtPct(pct)}</span>
                                <span
                                  className="inline-block h-1.5 w-12 rounded-sm bg-slate-100"
                                  style={{ position: "relative" }}
                                >
                                  <span
                                    className="absolute left-0 top-0 h-full rounded-sm"
                                    style={{
                                      width: `${Math.min(100, pct)}%`,
                                      background: COLOR.accent,
                                    }}
                                  />
                                </span>
                              </div>
                            </td>
                            <td className="py-1.5 pl-2 text-right text-[10px] text-slate-500 tabular-nums">
                              {m.last_worked ? formatDate(new Date(m.last_worked)) : "—"}
                            </td>
                          </>
                        )}
                      </tr>
                    )
                  })
              })()}
            </tbody>
          </table>
        )}
      </section>

      {/* ── Client Breakdown (admin only) ────────────────────────────────── */}
      {mode === "admin" && clients.length > 0 && (
        <section className="page-keep mb-5">
          <SectionHeading>
            Top Clients
            <span className="ml-2 text-[10px] font-normal text-slate-500">
              {clients.length} clients · ranked by hours
            </span>
          </SectionHeading>
          <table className="mt-2 w-full border-collapse text-[11px]">
            <thead>
              <tr className="border-b-2 border-slate-900 text-left text-[10px] uppercase tracking-wide text-slate-600">
                <th className="py-1.5 pr-2">Client</th>
                <th className="py-1.5 px-2 text-right">Matters</th>
                <th className="py-1.5 px-2 text-right">Hours</th>
                <th className="py-1.5 px-2 text-right">Revenue</th>
                <th className="py-1.5 pl-2 text-right">Share</th>
              </tr>
            </thead>
            <tbody>
              {clients
                .filter((c) => (c.hours ?? 0) > 0)
                .slice(0, 10)
                .map((c, i) => (
                  <tr key={i} className="border-b border-slate-100">
                    <td className="py-1.5 pr-2 font-medium text-slate-900">
                      {c.matter__clients__name || "Unknown"}
                    </td>
                    <td className="py-1.5 px-2 text-right tabular-nums">{c.matters_count}</td>
                    <td className="py-1.5 px-2 text-right tabular-nums">{fmtNum(c.hours)}h</td>
                    <td className="py-1.5 px-2 text-right font-semibold tabular-nums">
                      {fmtEur(c.revenue)}
                    </td>
                    <td className="py-1.5 pl-2 text-right tabular-nums">
                      <div className="flex items-center justify-end gap-1.5">
                        <span>{fmtPct(c.percentage)}</span>
                        <span
                          className="inline-block h-1.5 w-12 rounded-sm bg-slate-100"
                          style={{ position: "relative" }}
                        >
                          <span
                            className="absolute left-0 top-0 h-full rounded-sm"
                            style={{ width: `${Math.min(100, c.percentage)}%`, background: COLOR.accent }}
                          />
                        </span>
                      </div>
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </section>
      )}

      <footer className="mt-6 border-t border-slate-200 pt-3 text-[10px] text-slate-500">
        <div className="flex items-center justify-between">
          <span>
            {mode === "admin" ? "Confidential — internal use" : "Personal performance summary"}
          </span>
          <span>
            Sentra · Generated {formatDate(new Date())} · {periodLabel}
          </span>
        </div>
      </footer>

      {/* Print overrides */}
      <style>{`
        @media print {
          body { background: white !important; color: #000 !important; }
          .no-print { display: none !important; }
          aside, nav, header[data-layout-header] { display: none !important; }
          .printable-report {
            padding: 0 !important;
            margin: 0 !important;
            max-width: 100%;
            font-size: 10.5px !important;
          }
          .page-keep { break-inside: avoid; page-break-inside: avoid; }
          .page-break-before { break-before: page; page-break-before: always; }
          /* Recharts forces an SVG layer; ensure no clipping in print. */
          .recharts-wrapper, .recharts-surface { overflow: visible !important; }
        }
        @page { size: A4 landscape; margin: 12mm; }
      `}</style>
    </div>
  )
}

// ─── Sub-components ──────────────────────────────────────────────────────────

function SectionHeading({ children }: { children: React.ReactNode }) {
  return (
    <h2 className="text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-700">
      {children}
    </h2>
  )
}

function Stat({
  label,
  value,
  hint,
  accent,
  valueColor,
}: {
  label: string
  value: React.ReactNode
  hint?: string
  accent?: boolean
  valueColor?: string
}) {
  return (
    <div
      className={`rounded-md border p-3 ${
        accent ? "border-slate-900 bg-slate-50" : "border-slate-200 bg-white"
      }`}
    >
      <div className="text-[9px] font-semibold uppercase tracking-[0.12em] text-slate-500">
        {label}
      </div>
      <div
        className="mt-0.5 text-[20px] font-bold leading-tight tabular-nums"
        style={valueColor ? { color: valueColor } : undefined}
      >
        {value}
      </div>
      {hint && <div className="mt-0.5 text-[10px] text-slate-500">{hint}</div>}
    </div>
  )
}

function CompareStat({
  label,
  current,
  compare,
  format,
}: {
  label: string
  current: number
  compare: number
  format: "hours" | "pct" | "eur"
}) {
  const fmtV = (v: number) =>
    format === "hours" ? `${fmtNum(v)}h` : format === "pct" ? fmtPct(v) : fmtEur(v)
  const delta = compare > 0 ? ((current - compare) / compare) * 100 : current > 0 ? 100 : 0
  const up = delta > 0
  return (
    <div className="rounded-md border border-slate-200 bg-white p-3">
      <div className="text-[9px] font-semibold uppercase tracking-[0.12em] text-slate-500">
        {label}
      </div>
      <div className="mt-0.5 flex items-baseline justify-between gap-2">
        <span className="text-[16px] font-bold tabular-nums">{fmtV(current)}</span>
        <span
          className="text-[11px] font-semibold tabular-nums"
          style={{ color: up ? COLOR.positive : delta < 0 ? COLOR.danger : COLOR.neutral }}
        >
          {delta !== 0 ? `${up ? "+" : ""}${fmtNum(delta, 0)}%` : "—"}
        </span>
      </div>
      <div className="text-[10px] text-slate-500 tabular-nums">vs {fmtV(compare)}</div>
    </div>
  )
}

function ProgressBar({
  label,
  value,
  max,
  valueLabel,
  color,
}: {
  label: string
  value: number
  max: number
  valueLabel?: string
  color: string
}) {
  const pct = max > 0 ? Math.min(100, (value / max) * 100) : 0
  return (
    <div>
      <div className="flex items-baseline justify-between">
        <span className="text-[11px] uppercase tracking-wide text-slate-500">{label}</span>
        <span className="text-[12px] font-semibold tabular-nums">{valueLabel}</span>
      </div>
      <div className="mt-1 h-2 w-full rounded-sm bg-slate-100">
        <div
          className="h-full rounded-sm"
          style={{ width: `${pct}%`, background: color, transition: "none" }}
        />
      </div>
    </div>
  )
}
